import { action, observable, runInAction, computed, makeObservable } from "mobx";
import { handleStoreState, CoreStore } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API_URL } from "General/APIEndpoints";
import { download } from "utils/download";

const emptyData: API_MailResponse = {
  id: "",
  from: "",
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  excerpt: "",
  body: "",
  type: "INBOX",
  hasAttachments: false,
  timestamp: null,
  importance: "LOW",
  unread: false,
  abstractRecipients: {},
};

export class EmailDetailsStore extends CoreStore {
  data: API_MailResponse = emptyData;
  emailType: API_MAILRESPONSE_TYPE;
  projectSpecific: boolean;
  private emailId: string;

  constructor(options = undefined) {
    super(options);

    makeObservable(this, {
      data: observable,
      emailType: observable,
      projectSpecific: observable,
      init: action.bound,
      allowedToMoveToProject: computed,
      fetch: action,
      setData: action.bound,
      moveAttachToProject: action.bound,
      downloadAllFiles: action.bound,
    });
  }

  clear() {
    this.data = emptyData as API_MailResponse;
    this.emailType = undefined;
    this.projectSpecific = undefined;
    this.emailId = undefined;
  }

  async init(params: {
    emailType: API_MAILRESPONSE_TYPE;
    emailId: string;
    projectSpecific: boolean;
  }) {
    this.emailType = params.emailType;
    this.emailId = params.emailId;
    this.projectSpecific = params.projectSpecific;
    return await this.fetch();
  }

  url() {
    return API_URL.MAILS.getMail(projectsDataStore.projectId, this.emailType, this.emailId);
  }

  @handleStoreState()
  async fetch() {
    try {
      const params = this.projectSpecific ? { project: true } : undefined;
      const response = await API.getData<API_MailResponse>(this.url(), params);
      runInAction(() => {
        this.data = response;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async moveAttachToProject(filesMap: { [id: string]: string }, folderId: number) {
    try {
      await API.newData(
        API_URL.MAILS.moveAttachmentToProject(projectsDataStore.projectId, this.emailType, this.emailId, folderId),
        { ...filesMap },
        { project: this.projectSpecific }
      );
      await this.fetch();
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  setData(newData: API_MailResponse) {
    this.data = newData;
  }

  @handleStoreState()
  async downloadAllFiles() {
    try {
      // get file id's of accessible mail attachments
      const fileIds = this.data.attachments
        .filter(
          (item) =>
            (item.accessLevel === "DOWNLOAD" || item.accessLevel === "DOWNLOAD_WITH_WATERMARK") &&
            !item.deleted &&
            !item.malware
        )
        .map((item) => item.id);
      // if no files are left - throw an error
      if (!fileIds.length) {
        throw new Error("NO_FILES");
      }
      const formData: API_DownloadWizardProperties = {
        files: fileIds.map((fileId) => ({ id: +fileId, state: "ACTIVE" })),
        withDirectories: false,
        withWatermark: false,
        withQR: projectsDataStore.isQRForce(),
        pdfWherePossible: false,
      };
      const file = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formData
      );
      download(file, file.name);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Moving emails to project is allowed ony type INBOX or private SENT:
   * private inbox - all users
   * private sent - all users
   * project inbox - if mailSettings.projectInbox === 'WRITE'
   */
  get allowedToMoveToProject() {
    return (
      // if emails are of type INBOX or SENT
      (this.emailType === "INBOX" || this.emailType === "SENT") &&
      // and private (do not belong to project) - it's allowed
      (!this.projectSpecific ||
        // or user has required access level - it's allowed
        (projectsDataStore.currentProject.mailSettings &&
          projectsDataStore.currentProject.mailSettings.projectInbox === "WRITE"))
    );
  }
}

export const emailDetailsStore = new EmailDetailsStore();
